import { deleteDoc, doc } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { FirebaseError } from "firebase/app"
import { httpsCallable } from "firebase/functions"
import { db, storage, functions } from "@/config/firebase"
import { GallerySection } from "@/shared/types/product"

interface SaveProductsResponse {
  saved: number
  skipped: number
  message: string
}

interface GenerateProductTextResponse {
  name: string
  description: string
  keywords: string[]
}
interface UpdateProductDetailsRequest {
  productId: string
  name: string
  description: string
  keywords: string[]
  category: string
  price: string
}
interface UpdateProductDetailsResponse {
  name: string
  description: string
  keywords: string[]
  category: string
  price: string
}
type RegenerateImageErrorCode =
  | "resource-exhausted"
  | "invalid-argument"
  | "not-found"
  | "failed-precondition"
  | "internal"
  | "unknown"

interface RegenerateProductImageRequest {
  productId: string
  generatedImageIndex: number
  promptId?: string
  customPrompt?: string
}

interface RegenerateProductImageResponse {
  imageUrl: string
}

const MAX_TEXT_IMAGE_BYTES = 4 * 1024 * 1024
const MAX_TEXT_IMAGE_DIMENSION = 1600
const ALLOWED_TEXT_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/heic"])
class ProductImageRegenerationError extends Error {
  code: RegenerateImageErrorCode

  constructor(code: RegenerateImageErrorCode, message: string) {
    super(message)
    this.name = "ProductImageRegenerationError"
    this.code = code
  }
}

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read image file."))
    reader.readAsDataURL(file)
  })

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error("Failed to load image for AI text generation."))
    image.src = src
  })

const dataUrlToBase64 = (dataUrl: string): string => dataUrl.split(",")[1] ?? ""

const estimateBase64Bytes = (base64: string): number => {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0
  return Math.floor((base64.length * 3) / 4) - padding
}

const compressImageToBase64 = async (file: File): Promise<{ imageBase64: string; mimeType: string }> => {
  const originalDataUrl = await readFileAsDataUrl(file)
  const originalBase64 = dataUrlToBase64(originalDataUrl)

  if (estimateBase64Bytes(originalBase64) <= MAX_TEXT_IMAGE_BYTES && ALLOWED_TEXT_IMAGE_MIME_TYPES.has(file.type)) {
    return { imageBase64: originalBase64, mimeType: file.type || "image/jpeg" }
  }

  const image = await loadImage(originalDataUrl)
  const scale = Math.min(1, MAX_TEXT_IMAGE_DIMENSION / Math.max(image.width, image.height))
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(image.width * scale))
  canvas.height = Math.max(1, Math.round(image.height * scale))

  const context = canvas.getContext("2d")
  if (!context) {
    throw new Error("Failed to prepare image for AI text generation.")
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height)

  let quality = 0.85
  let compressedDataUrl = canvas.toDataURL("image/jpeg", quality)
  let compressedBase64 = dataUrlToBase64(compressedDataUrl)

  while (estimateBase64Bytes(compressedBase64) > MAX_TEXT_IMAGE_BYTES && quality > 0.4) {
    quality -= 0.1
    compressedDataUrl = canvas.toDataURL("image/jpeg", quality)
    compressedBase64 = dataUrlToBase64(compressedDataUrl)
  }

  return { imageBase64: compressedBase64, mimeType: "image/jpeg" }
}
const normalizeCallableError = (error: unknown, fallbackMessage: string): ProductImageRegenerationError => {
  if (error instanceof ProductImageRegenerationError) {
    return error
  }

  const firebaseCode =
    error instanceof FirebaseError
      ? error.code.replace(/^functions\//, "")
      : error instanceof Error
        ? "unknown"
        : "unknown"
  const message = error instanceof Error && error.message ? error.message : fallbackMessage

  const code: RegenerateImageErrorCode =
    firebaseCode === "resource-exhausted" ||
    firebaseCode === "invalid-argument" ||
    firebaseCode === "not-found" ||
    firebaseCode === "failed-precondition" ||
    firebaseCode === "internal"
      ? firebaseCode
      : "unknown"

  return new ProductImageRegenerationError(code, message || fallbackMessage)
}

/**
 * Upload product images to Firebase Storage (client-side) and then save
 * product docs via the server-side callable (which enforces quota).
 */
const saveProducts = async (userId: string, sections: GallerySection[]): Promise<SaveProductsResponse> => {
  const uploadBatchTimestamp = new Date().toISOString().replace(/[:.]/g, "-")

  // Step 1: upload all images to Storage (client has write access to its own folder)
  const productsWithUrls = await Promise.all(
    sections.map(async section => {
      const imageUrls = await Promise.all(
        section.files.map(async (file, i) => {
          const storageRef = ref(storage, `${userId}/images/${uploadBatchTimestamp}/og/${i}_${file.name}`)
          await uploadBytes(storageRef, file)
          return getDownloadURL(storageRef)
        })
      )
      return {
        imageUrls,
        name: section.name,
        description: section.description,
        keywords: section.keywords,
        category: section.category,
        price: section.price,
        selectedPromptIds: section.selectedPromptIds
      }
    })
  )

  // Step 2: server-side callable enforces quota and creates Firestore docs
  const saveProductsCallable = httpsCallable<{ products: typeof productsWithUrls }, SaveProductsResponse>(
    functions,
    "saveProducts"
  )
  const result = await saveProductsCallable({ products: productsWithUrls })
  return result.data
}

const generateProductText = async (imageFile: File): Promise<GenerateProductTextResponse> => {
  const { imageBase64, mimeType } = await compressImageToBase64(imageFile)
  const generateProductTextCallable = httpsCallable<
    { imageBase64: string; mimeType: string },
    GenerateProductTextResponse
  >(functions, "generateProductText")

  const result = await generateProductTextCallable({ imageBase64, mimeType })
  return result.data
}

/**
 * Delete a single product document from Firestore.
 */
const deleteProduct = async (userId: string, productId: string): Promise<void> => {
  await deleteDoc(doc(db, "users", userId, "products", productId))
}

const regenerateProductImage = async ({
  productId,
  generatedImageIndex,
  promptId,
  customPrompt
}: RegenerateProductImageRequest): Promise<RegenerateProductImageResponse> => {
  try {
    const regenerateImageCallable = httpsCallable<RegenerateProductImageRequest, RegenerateProductImageResponse>(
      functions,
      "regenerateImage"
    )
    const result = await regenerateImageCallable({
      productId,
      generatedImageIndex,
      promptId,
      customPrompt
    })
    return result.data
  } catch (error) {
    throw normalizeCallableError(error, "Failed to regenerate image. Please try again.")
  }
}

const updateProductDetails = async (payload: UpdateProductDetailsRequest): Promise<UpdateProductDetailsResponse> => {
  const updateProductCallable = httpsCallable<UpdateProductDetailsRequest, UpdateProductDetailsResponse>(
    functions,
    "updateProduct"
  )
  const result = await updateProductCallable(payload)
  return result.data
}

interface PushListingRequest {
  brand: string
  externalProductId?: string
  externalProductIdType?: "ean" | "gtin" | "isbn" | "upc"
  hasIdentifierExemption: boolean
  price: number
  productId: string
  productType: string
  quantity: number
  sku: string
}

interface InitAmazonOAuthResponse {
  url: string
}

interface PushListingResponse {
  issues: Array<{ code?: string; message?: string; severity?: string }>
  marketplaceName: string
  sku: string
  status: string
  submissionId: string | null
}

interface AmazonConnectionStatus {
  available?: boolean
  connected: boolean
  marketplaceCode?: string | null
  marketplaceName?: string | null
  sellerAccountEnding?: string | null
}

/**
 * Push a saved product to Amazon as a listing via the SP-API callable.
 */
const pushProductToAmazon = async (payload: PushListingRequest): Promise<PushListingResponse> => {
  const pushListingCallable = httpsCallable<PushListingRequest, PushListingResponse>(functions, "pushListingToAmazon")
  const result = await pushListingCallable(payload)
  return result.data
}

/**
 * Starts Amazon OAuth and returns the Seller Central consent URL.
 */
const initAmazonOAuth = async (marketplaceCode: string): Promise<InitAmazonOAuthResponse> => {
  const initAmazonOAuthCallable = httpsCallable<{ marketplaceCode: string }, InitAmazonOAuthResponse>(
    functions,
    "initAmazonOAuth"
  )
  const result = await initAmazonOAuthCallable({ marketplaceCode })
  return result.data
}

const getAmazonConnectionStatus = async (): Promise<AmazonConnectionStatus> => {
  const callable = httpsCallable<Record<string, never>, AmazonConnectionStatus>(functions, "getAmazonConnectionStatus")
  const result = await callable({})
  return result.data
}

const disconnectAmazon = async (): Promise<void> => {
  const callable = httpsCallable<Record<string, never>, { connected: false }>(functions, "disconnectAmazon")
  await callable({})
}

export {
  saveProducts,
  deleteProduct,
  generateProductText,
  regenerateProductImage,
  updateProductDetails,
  initAmazonOAuth,
  getAmazonConnectionStatus,
  disconnectAmazon,
  pushProductToAmazon,
  ProductImageRegenerationError
}
export type {
  AmazonConnectionStatus,
  PushListingRequest,
  PushListingResponse,
  RegenerateImageErrorCode,
  RegenerateProductImageRequest,
  RegenerateProductImageResponse
}
